/**
 * Utilities for multi-page website slug management
 */

/** Slugs reserved for platform routes — cannot be used as page slugs */
export const RESERVED_PAGE_SLUGS = new Set([
  'donate', 'edit', 'coach', 'coaching', 'library',
  'dashboard', 'admin', 'auth', 'api', 'me', 'blog',
]);

/**
 * Convert a title string into a valid URL slug.
 * Lowercase, replace spaces/special chars with hyphens, collapse consecutive hyphens,
 * trim leading/trailing hyphens. Max 48 chars.
 * @param {string} title
 * @returns {string}
 */
export const formatSlug = (title) => {
  if (!title) return '';
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')   // replace non-alphanumeric runs with hyphen
    .replace(/-{2,}/g, '-')         // collapse consecutive hyphens
    .replace(/^-|-$/g, '')          // trim leading/trailing hyphens
    .slice(0, 48);
};

/**
 * Validate a page slug.
 * @param {string} slug
 * @param {Object} existingPages — map of slug → page data (keys are existing slugs)
 * @returns {{ valid: boolean, error?: string }}
 */
export const validatePageSlug = (slug, existingPages = {}) => {
  if (!slug) {
    return { valid: false, error: 'Page slug cannot be empty' };
  }
  if (slug.length > 48) {
    return { valid: false, error: 'Slug must be 48 characters or fewer' };
  }
  if (!/^[a-z0-9][a-z0-9-]*$/.test(slug)) {
    return { valid: false, error: 'Slug must start with a letter or number and contain only lowercase letters, numbers, and hyphens' };
  }
  if (/--/.test(slug)) {
    return { valid: false, error: 'Slug cannot contain consecutive hyphens' };
  }
  if (RESERVED_PAGE_SLUGS.has(slug)) {
    return { valid: false, error: `"${slug}" is a reserved page name` };
  }
  if (Object.prototype.hasOwnProperty.call(existingPages, slug)) {
    return { valid: false, error: `A page with slug "${slug}" already exists` };
  }
  return { valid: true };
};

/**
 * Resolve the public URL path for a page.
 * @param {string|null|undefined} slug — page slug, or null/undefined for home
 * @param {string} username
 * @param {boolean} isOnOwnDomain — true when the profile is on a custom domain
 * @returns {string}
 */
export const resolvePageUrl = (slug, username, isOnOwnDomain) => {
  if (!slug || slug === '__home') {
    return isOnOwnDomain ? '/' : `/me/${username}`;
  }
  // Feature pages have dedicated routes
  if (slug === '__coaching') return isOnOwnDomain ? '/coach' : `/me/${username}/coach`;
  if (slug === '__library') return isOnOwnDomain ? '/library' : `/me/${username}/library`;
  if (slug === '__blog') return isOnOwnDomain ? '/blog' : `/me/${username}/blog`;
  return isOnOwnDomain ? `/${slug}` : `/me/${username}/${slug}`;
};

